import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Heart, ShieldCheck, Users } from "lucide-react";
import AppLogo from "@/components/AppLogo";
import Reveal from "@/components/Reveal";
import { HERO_ABOUT } from "@/config/heroImages";

const VALUES = [
  {
    icon: Users,
    title: "Community first",
    desc: "We bring societies, students and businesses together on one trusted platform.",
  },
  {
    icon: ShieldCheck,
    title: "Verified & safe",
    desc: "Every student account is tied to a verified university email.",
  },
  {
    icon: Heart,
    title: "Made for students",
    desc: "Free to use, built around the way Hong Kong students actually live.",
  },
];

/**
 * Public "About" page.
 */
const AboutPage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div>
      <section
        className="relative bg-cover bg-center py-16 md:py-20"
        style={{ backgroundImage: `url(${HERO_ABOUT})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/40 to-[#ffa217]/20" />
        <div className="relative z-10 mx-auto max-w-3xl animate-fade-down px-4 text-center">
          <AppLogo size={64} className="mx-auto rounded-2xl" />
          <h1 className="mt-6 text-4xl font-extrabold text-white md:text-5xl">
            {t("About Uninfo HK")}
          </h1>
          <p className="mt-5 text-lg leading-relaxed text-gray-200">
            {t("Uninfo HK is a campus life platform connecting Hong Kong university students with events, societies, surveys and partner businesses — all in one place.")}
          </p>
        </div>
      </section>

      <section className="bg-white py-16 md:py-20">
        <Reveal className="mx-auto max-w-3xl px-4">
          <h2 className="text-3xl font-extrabold text-[#333]">{t("Our mission")}</h2>
          <p className="mt-4 leading-relaxed text-gray-600">
            {t("University life is full of opportunities — but they are scattered across apps, posters and word of mouth. Our mission is to bring every event, society and offer onto one simple platform, so students can spend less time searching and more time doing.")}
          </p>
          <p className="mt-4 leading-relaxed text-gray-600">
            {t("Today we serve students across all major Hong Kong universities, and we are just getting started.")}
          </p>
        </Reveal>
      </section>

      <section className="bg-[#fff8f0] py-16 md:py-20">
        <div className="mx-auto max-w-6xl px-4">
          <h2 className="text-center text-3xl font-extrabold text-[#333]">
            {t("What we value")}
          </h2>
          <Reveal className="mt-12 grid gap-6 md:grid-cols-3">
            {VALUES.map((value) => (
              <div key={value.title} className="rounded-2xl border border-gray-100 bg-white p-8 text-center">
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#fff3e0] text-[#ffa217]">
                  <value.icon size={26} />
                </span>
                <h3 className="mt-5 text-xl font-bold text-[#333]">{t(value.title)}</h3>
                <p className="mt-2 leading-relaxed text-gray-600">{t(value.desc)}</p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-16">
        <Reveal className="mx-auto max-w-3xl px-4 text-center">
          <h2 className="text-3xl font-extrabold text-[#333]">
            {t("Ready to explore?")}
          </h2>
          <Link
            to="/signup"
            className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[#ffa217] px-8 py-3 font-bold text-white shadow-lg shadow-[#ffa217]/30 transition hover:bg-[#e08f0e]"
          >
            {t("Join as Student")}
            <ArrowRight size={18} />
          </Link>
        </Reveal>
      </section>
    </div>
  );
};

export default AboutPage;